// websocketSaga — bridges the WebSocketService into Redux via eventChannel.
//
// Routes incoming WebSocket messages to the same slice actions used by
// handleImportUserFiles so topology/scenario data is cached identically
// regardless of source (upload vs live).

import { eventChannel, buffers, type EventChannel } from 'redux-saga';
import { take, put, call, fork, cancel, takeLatest, select } from 'redux-saga/effects';
import { createAction, type PayloadAction } from '@reduxjs/toolkit';
import { loadTopology } from '../slices/topologySlice.ts';
import { loadScenario } from '../actions/loadScenario.ts';
import {
  manifestLoaded,
  scenarioLoaded,
  topologyLoaded,
  setActiveScenario,
  setActiveSource,
  clearSource,
  wsConnectionStatusChanged,
  wsSessionEstablished,
  wsErrorReceived,
  wsErrorDismissed,
  wsDisconnected,
  wsRawManifestStored,
  selectActiveSource,
  selectActiveFlowId,
  selectActiveScenarioId,
  findFirstHappyPath,
  type ScenarioSourceId,
} from '../slices/scenarioSourceSlice.ts';
import { clearSuiteResults } from '../slices/simulationSlice.ts';
import { setAppView } from '../slices/uiSlice.ts';
import { webSocketService } from '../../services/WebSocketService.ts';
import { classifyScenarioLabel } from '../../components/simulation/scenarioBadges.ts';
import {
  validateTopology as validateTopologyContract,
  validateSeed,
  validateSeedStructure,
  formatValidationErrors,
} from '../../contracts/moment-ir-validation.ts';
import type { RootState } from '../index.ts';
import type {
  WsServerMessage,
  WsConnectionStatus,
  WsManifest,
  WsErrorPhase,
} from '../../types/websocket.ts';
import type {
  FlowManifest,
  FlowEntry,
  ManifestEntry,
  ScenarioKind,
  SimulationScenario,
  SimulationTopology,
} from '../../types/index.ts';

// ---------------------------------------------------------------------------
// Intent actions — dispatched by components
// ---------------------------------------------------------------------------

export const wsConnect = createAction<{ host: string; port: number; secure: boolean }>(
  'websocket/connect',
);
export const wsDisconnect = createAction('websocket/disconnect');

// ---------------------------------------------------------------------------
// Internal channel event types
// ---------------------------------------------------------------------------

type ChannelEvent =
  | { kind: 'message'; data: WsServerMessage }
  | { kind: 'status'; status: WsConnectionStatus }
  | { kind: 'error'; message: string };

// ---------------------------------------------------------------------------
// Event channel factory
// ---------------------------------------------------------------------------

function createWsChannel(host: string, port: number, secure: boolean): EventChannel<ChannelEvent> {
  return eventChannel(
    (emitter) => {
      webSocketService.connect(host, port, secure, {
        onMessage: (msg) => emitter({ kind: 'message', data: msg }),
        onStatusChange: (status) => emitter({ kind: 'status', status }),
        onError: (message) => emitter({ kind: 'error', message }),
      });

      // Unsubscribe function — called when channel is closed
      return () => {
        webSocketService.disconnect();
      };
    },
    // Expanding buffer: eventChannel's default buffer drops messages emitted
    // while no taker is pending (e.g. bursts during a long handler) — a
    // latent message-loss bug for the live bridge.
    buffers.expanding(),
  );
}

// ---------------------------------------------------------------------------
// Scenario flag derivation — explicit metadata first, exact-prefix fallback
// ---------------------------------------------------------------------------

const KNOWN_SCENARIO_KINDS: readonly ScenarioKind[] = [
  'happy',
  'variant',
  'failure',
  'negative',
  'timeout',
];

/**
 * Derive isHappyPath/isNegative for a scenario coming over the WS bridge.
 * Feature-detected in priority order:
 *  1. `kind` field (new Moment contract: 'happy' | 'variant' | 'failure' |
 *     'negative' | 'timeout') — recognized kinds are passed THROUGH so the
 *     derived manifest entry carries them (timeout chips, etc.); unknown
 *     future kinds still drive the flags but are not propagated as typed kind;
 *  2. explicit `isHappyPath` / `isNegative` booleans on the scenario;
 *  3. exact-prefix label classification (never substring — "Unhappy path"
 *     must not read as happy).
 */
export function deriveScenarioFlags(scenario: SimulationScenario): {
  isHappyPath: boolean;
  isNegative: boolean;
  kind?: ScenarioKind;
} {
  const raw = scenario as unknown as Record<string, unknown>;
  if (typeof raw.kind === 'string') {
    const flags = { isHappyPath: raw.kind === 'happy', isNegative: raw.kind === 'negative' };
    return KNOWN_SCENARIO_KINDS.includes(raw.kind as ScenarioKind)
      ? { ...flags, kind: raw.kind as ScenarioKind }
      : flags;
  }
  if (typeof raw.isHappyPath === 'boolean' || typeof raw.isNegative === 'boolean') {
    return { isHappyPath: raw.isHappyPath === true, isNegative: raw.isNegative === true };
  }
  const byLabel = classifyScenarioLabel(scenario.scenarioLabel ?? '');
  return { isHappyPath: byLabel === 'happy', isNegative: byLabel === 'negative' };
}

// ---------------------------------------------------------------------------
// Deep validation helpers — pure, unit-testable. Invalid payloads are NEVER
// loaded silently; each rejection carries a visible error for the WS overlay.
// ---------------------------------------------------------------------------

export interface WsValidationRejection {
  phase: WsErrorPhase;
  message: string;
}

/** Validate an incoming topology; null = valid, otherwise a visible rejection. */
export function rejectInvalidTopology(
  flowId: string,
  topology: SimulationTopology,
): WsValidationRejection | null {
  const result = validateTopologyContract(topology);
  if (result.valid) return null;
  return {
    phase: 'topology',
    message: `Topology for flow "${flowId}" failed validation and was not loaded: ${formatValidationErrors(result)}`,
  };
}

/**
 * Validate an incoming scenario — full seed-vs-topology validation when the
 * flow's topology is available (and itself valid), envelope-only otherwise.
 * null = valid, otherwise a visible rejection.
 */
export function rejectInvalidScenario(
  scenario: SimulationScenario,
  topology: SimulationTopology | undefined,
): WsValidationRejection | null {
  const result = topology ? validateSeed(scenario, topology) : validateSeedStructure(scenario);
  if (result.valid) return null;
  return {
    phase: 'simulate',
    message: `Scenario "${scenario.scenarioId}" failed validation and was not loaded: ${formatValidationErrors(result)}`,
  };
}

// ---------------------------------------------------------------------------
// Manifest builder — converts lightweight WsManifest to a full FlowManifest
// by deriving ManifestEntry[] from the scenario data keyed by flowId.
// ---------------------------------------------------------------------------

export function buildFlowManifest(
  wsManifest: WsManifest,
  scenarios: Record<string, SimulationScenario>,
): FlowManifest {
  const validFlowIds = new Set(wsManifest.flows.map((f) => f.flowId));

  // Group scenarios by flowId — skip scenarios with missing or unknown flowId
  const scenariosByFlow = new Map<string, SimulationScenario[]>();
  for (const scenario of Object.values(scenarios)) {
    const flowId = scenario.flowId;
    if (!flowId || !validFlowIds.has(flowId)) continue;
    const list = scenariosByFlow.get(flowId) ?? [];
    list.push(scenario);
    scenariosByFlow.set(flowId, list);
  }

  const flows: FlowEntry[] = wsManifest.flows.map((wf) => {
    const flowScenarios = scenariosByFlow.get(wf.flowId) ?? [];
    const entries: ManifestEntry[] = flowScenarios.map((sc) => ({
      scenarioId: sc.scenarioId,
      scenarioLabel: sc.scenarioLabel,
      description: sc.description,
      file: `${sc.scenarioId}.json`,
      eventCount: sc.events.length,
      pathLength: sc.expectedPath.length,
      branchCount: Array.isArray(sc.activeBranches) ? sc.activeBranches.length : 0,
      ...deriveScenarioFlags(sc),
    }));

    return {
      flowId: wf.flowId,
      flowName: wf.flowName,
      topology: wf.topology,
      scenarios: entries,
    };
  });

  return {
    flows,
    artifacts: wsManifest.artifacts,
  };
}

// ---------------------------------------------------------------------------
// Message routing — maps WS messages to Redux actions
// ---------------------------------------------------------------------------

const WS_SOURCE: ScenarioSourceId = 'websocket';

function* handleInitialLoad(msg: WsServerMessage & { type: 'initial-load' }) {
  // Clear previous websocket data
  yield put(clearSource(WS_SOURCE));

  yield put(wsSessionEstablished({ sessionId: msg.sessionId, specFile: msg.specFile }));

  // Store raw WS manifest for later rebuilds (e.g., scenario-update)
  yield put(wsRawManifestStored(msg.manifest));

  // Load all topologies — key by manifest flowId, resolve via manifest topology
  // filename. Deep-validate BEFORE dispatching topologyLoaded: an invalid
  // topology is rejected with a visible error, never loaded silently.
  const topologiesByFlowId = new Map<string, SimulationTopology>();
  for (const flow of msg.manifest.flows) {
    const topology = msg.topologies[flow.topology];
    if (topology === undefined) continue;
    const rejection = rejectInvalidTopology(flow.flowId, topology);
    if (rejection) {
      yield put(wsErrorReceived(rejection));
      continue;
    }
    topologiesByFlowId.set(flow.flowId, topology);
    yield put(topologyLoaded({ source: WS_SOURCE, flowId: flow.flowId, data: topology }));
  }

  // Validate + load all scenarios — re-key by scenarioId from the scenario
  // itself (msg keys are filenames). Invalid scenarios are rejected visibly
  // and excluded from the derived manifest.
  const scenariosByIds = new Map<string, SimulationScenario>();
  const validScenarios: Record<string, SimulationScenario> = {};
  for (const [, scenario] of Object.entries(msg.scenarios)) {
    const flowTopology = scenario.flowId ? topologiesByFlowId.get(scenario.flowId) : undefined;
    const rejection = rejectInvalidScenario(scenario, flowTopology);
    if (rejection) {
      yield put(wsErrorReceived(rejection));
      continue;
    }
    scenariosByIds.set(scenario.scenarioId, scenario);
    validScenarios[scenario.scenarioId] = scenario;
    yield put(
      scenarioLoaded({ source: WS_SOURCE, scenarioId: scenario.scenarioId, data: scenario }),
    );
  }

  // Build a full FlowManifest from the lightweight WS manifest + the VALID
  // scenario subset (rejected scenarios must not appear as loadable entries).
  const manifest = buildFlowManifest(msg.manifest, validScenarios);
  yield put(manifestLoaded({ source: WS_SOURCE, manifest }));

  // A new live session replaces the websocket source's data wholesale —
  // suite rows keyed by a previous manifest's scenarioIds are stale truth.
  yield put(clearSuiteResults());

  // Auto-select first happy path and load it (if data present)
  const happyPath = findFirstHappyPath(manifest);
  if (happyPath) {
    const [flow, entry] = happyPath;
    const topology = topologiesByFlowId.get(flow.flowId);
    const scenario = scenariosByIds.get(entry.scenarioId);

    if (topology !== undefined && scenario !== undefined) {
      yield put(loadTopology(topology));
      yield put(
        setActiveScenario({
          source: WS_SOURCE,
          scenarioId: entry.scenarioId,
          flowId: flow.flowId,
        }),
      );
      // NOTE: no resetSimulation/resetEvents — loadScenario extraReducers
      // reset both slices while preserving coverage and suite results.
      yield put(loadScenario({ seed: scenario }));
    }
  }

  // v2 IA: if the user connected from the landing screen, a successful
  // initial load lands them in the portfolio ledger (whose entry auto-replays
  // the suite). Guarded to 'landing' so a live-session refresh mid-session
  // never yanks the user away from the screen they are reading. The optional
  // access tolerates harness stores composed without the ui slice.
  const appView: string | undefined = yield select(
    (state: Partial<RootState>) => state.ui?.appView,
  );
  if (appView === 'landing') {
    yield put(setAppView('portfolio'));
  }
}

function* handleTopologyUpdate(msg: WsServerMessage & { type: 'topology-update' }) {
  // Deep-validate before caching: an invalid live edit surfaces a visible
  // error and the previously loaded topology stays in place — the update is
  // never applied silently.
  const rejection = rejectInvalidTopology(msg.flowId, msg.topology);
  if (rejection) {
    yield put(wsErrorReceived(rejection));
    return;
  }

  // Cache the updated topology
  yield put(topologyLoaded({ source: WS_SOURCE, flowId: msg.flowId, data: msg.topology }));

  // Store updated raw WS manifest and rebuild the derived FlowManifest
  yield put(wsRawManifestStored(msg.manifest));
  const cachedScenarios: Record<string, SimulationScenario> = yield select(
    (s: RootState) => s.scenarioSource.websocket.loadedScenarios,
  );
  const manifest = buildFlowManifest(msg.manifest, cachedScenarios);
  yield put(manifestLoaded({ source: WS_SOURCE, manifest }));

  // Only re-render if user is viewing this source and this flow
  const activeSource: ScenarioSourceId = yield select(selectActiveSource);
  const activeFlowId: string | null = yield select((s: RootState) => selectActiveFlowId(s));
  if (activeSource === 'websocket' && activeFlowId === msg.flowId) {
    yield put(loadTopology(msg.topology));
  }
}

function* handleScenarioUpdate(msg: WsServerMessage & { type: 'scenario-update' }) {
  // Cache all incoming scenarios — normalize flowId from the message envelope
  // since SimulationScenario.flowId is optional but the WS protocol provides
  // it. Deep-validate each scenario against the flow's cached topology before
  // dispatching; invalid updates surface a visible error and are not cached.
  const cachedTopologies: Record<string, SimulationTopology> = yield select(
    (s: RootState) => s.scenarioSource.websocket.loadedTopologies,
  );
  const acceptedScenarioIds = new Set<string>();
  for (const [, scenario] of Object.entries(msg.scenarios)) {
    const normalized = scenario.flowId ? scenario : { ...scenario, flowId: msg.flowId };
    const rejection = rejectInvalidScenario(
      normalized,
      normalized.flowId ? cachedTopologies[normalized.flowId] : undefined,
    );
    if (rejection) {
      yield put(wsErrorReceived(rejection));
      continue;
    }
    acceptedScenarioIds.add(normalized.scenarioId);
    yield put(
      scenarioLoaded({ source: WS_SOURCE, scenarioId: normalized.scenarioId, data: normalized }),
    );
  }

  // Rebuild manifest so the drawer tree and ScenarioNav reflect updated labels/counts
  const rawWsManifest: WsManifest | null = yield select(
    (s: RootState) => s.scenarioSource.websocket.rawWsManifest,
  );
  if (rawWsManifest) {
    const allScenarios: Record<string, SimulationScenario> = yield select(
      (s: RootState) => s.scenarioSource.websocket.loadedScenarios,
    );
    const manifest = buildFlowManifest(rawWsManifest, allScenarios);
    yield put(manifestLoaded({ source: WS_SOURCE, manifest }));
  }

  // If the currently active scenario was updated (and accepted), reload it
  // for live re-render — a rejected update must not replace the loaded seed.
  const activeSource: ScenarioSourceId = yield select(selectActiveSource);
  const activeScenarioId: string | null = yield select(selectActiveScenarioId);
  if (activeSource === 'websocket' && activeScenarioId) {
    for (const scenario of Object.values(msg.scenarios)) {
      if (scenario.scenarioId === activeScenarioId && acceptedScenarioIds.has(activeScenarioId)) {
        // NOTE: no resetSimulation/resetEvents — loadScenario extraReducers
        // reset both slices while preserving coverage and suite results.
        yield put(loadScenario({ seed: scenario }));
        break;
      }
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function* handleArtifactUpdate(_msg: WsServerMessage & { type: 'artifact-update' }) {
  // Artifacts (eventCatalog, impactAnalysis, sagaStateMachines) are consumed
  // by enrichment services. For now we acknowledge receipt; artifact caching
  // can be wired up when the catalog/impact panels consume from Redux.
}

function* handleWsError(msg: WsServerMessage & { type: 'error' }) {
  yield put(wsErrorReceived({ phase: msg.phase, message: msg.message }));
}

// ---------------------------------------------------------------------------
// Channel consumer — reads events from the WebSocket eventChannel
// ---------------------------------------------------------------------------

function* watchChannel(channel: EventChannel<ChannelEvent>) {
  let lastStatus: WsConnectionStatus | null = null;

  try {
    while (true) {
      const event: ChannelEvent = yield take(channel);

      switch (event.kind) {
        case 'status':
          lastStatus = event.status;
          // Use wsDisconnected() for disconnected status to clear session metadata
          if (event.status === 'disconnected') {
            yield put(wsDisconnected());
          } else {
            yield put(wsConnectionStatusChanged(event.status));
          }
          break;

        case 'error':
          // Don't override a terminal 'disconnected' with 'error' (e.g., clean shutdown 1000
          // emits disconnected then error — preserve the disconnected state).
          if (lastStatus !== 'disconnected') {
            lastStatus = 'error';
            yield put(wsConnectionStatusChanged('error'));
          }
          yield put(wsErrorReceived({ phase: 'connection', message: event.message }));
          break;

        case 'message': {
          // Dismiss any previous error on a successful message
          yield put(wsErrorDismissed());

          switch (event.data.type) {
            case 'initial-load':
              yield call(handleInitialLoad, event.data);
              break;
            case 'topology-update':
              yield call(handleTopologyUpdate, event.data);
              break;
            case 'scenario-update':
              yield call(handleScenarioUpdate, event.data);
              break;
            case 'artifact-update':
              yield call(handleArtifactUpdate, event.data);
              break;
            case 'error':
              yield call(handleWsError, event.data);
              break;
          }
          break;
        }
      }
    }
  } finally {
    channel.close();
  }
}

// ---------------------------------------------------------------------------
// Connection handler
// ---------------------------------------------------------------------------

// Channel task managed within the root saga scope (not module-level) to
// avoid shared mutable state across HMR reloads or multiple saga mounts.
// We use an object wrapper since inner generator functions can't capture
// reassigned `let` bindings from the outer generator scope.

export default function* websocketSaga() {
  // Task ref scoped to this saga instance (safe across HMR / multiple mounts).
  // Typed as `unknown` because redux-saga's `Task` type is not exported from
  // the effects module; cancel() accepts `any` at runtime.
  const taskRef: { current: unknown } = { current: null };

  function* handleWsConnect(
    action: PayloadAction<{ host: string; port: number; secure: boolean }>,
  ) {
    const { host, port, secure } = action.payload;

    // Cancel any existing connection
    if (taskRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      yield cancel(taskRef.current as any);
      taskRef.current = null;
    }

    // Switch active source to websocket
    yield put(setActiveSource(WS_SOURCE));

    const channel: EventChannel<ChannelEvent> = yield call(createWsChannel, host, port, secure);
    taskRef.current = yield fork(watchChannel, channel);
  }

  function* handleWsDisconnect() {
    if (taskRef.current) {
      // Cancelling the task closes the eventChannel, whose unsubscribe
      // callback calls webSocketService.disconnect() — no need to call it here.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      yield cancel(taskRef.current as any);
      taskRef.current = null;
    }
    // Use wsDisconnected to clear session/specFile alongside status
    yield put(wsDisconnected());
  }

  yield takeLatest(wsConnect.type, handleWsConnect);
  yield takeLatest(wsDisconnect.type, handleWsDisconnect);
}
